import _ from 'lodash';
import React, { useCallback, useEffect } from 'react';
import BarcodeScannerComponent from 'react-qr-barcode-scanner';

import {
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import { FormControlWrapper } from '../../primitives/textInput/styles';
import { configs } from '$configs';
import intl from '$gintl';

interface ScannerProps {
  onScanSuccess: Function;
  mobileDefault?: boolean;
  className?: string;
  enableMobile?: boolean;
  minLength?: number;
  waitScanTime?: number;
  debounceThres?: number;
  inputSize?: string;
  variant?: 'text' | 'outlined' | 'filled';
}

const Scanner: React.FC<ScannerProps> = ({
  mobileDefault = false,
  className = '',
  minLength = 6,
  enableMobile = true,
  debounceThres = 500,
  waitScanTime = 1000,
  inputSize = configs.display.inputSize || 'small',
  variant = configs.display.inputType || 'outlined',
  onScanSuccess,
}) => {
  const [device, setDevice] = React.useState<any>({});
  const [devices, setDevices] = React.useState<any>([]);
  const [disableScan, setDisableScan] = React.useState(false);
  const [scanValue, setScanValue] = React.useState('');
  const [mobileMode, setMobileMode] = React.useState(mobileDefault);

  const handleDevices = useCallback(
    (mediaDevices) => {
      const videoDevices = mediaDevices.filter(({ kind }) => kind === 'videoinput');
      setDevices(videoDevices);
      if (!videoDevices.length) return;
      const device = videoDevices.find(({ label }) => label.includes('back')) || videoDevices[0];
      setDevice(device);
    },
    [setDevices],
  );

  useEffect(() => {
    if (disableScan) setTimeout(() => setDisableScan(false), waitScanTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableScan]);

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(handleDevices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleDevices]);

  const onAcceptScan = (value) => {
    if (value?.length < minLength) return;
    onScanSuccess(value);
    setScanValue('');
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceScan = useCallback(_.debounce(onAcceptScan, debounceThres), []);

  const onChangeScanText = (e) => {
    setScanValue(e.target.value);
    debounceScan(e.target.value);
  };

  return (
    <>
      <FormControlWrapper
        className={`${className} ${inputSize === 'xsmall' && 'xsmall'}`}
        size={inputSize}
        variant={variant}
        fullWidth>
        {devices.length > 0 && enableMobile && (
          <>
            <FormControlLabel
              className={className}
              control={
                <Switch
                  className="scan-switch"
                  checked={mobileMode}
                  onChange={() => setMobileMode(!mobileMode)}
                />
              }
              label={intl('INPUT.LABEL.SCAN_MOBILE')}
            />
            {mobileMode && (
              <>
                <FormControl size="small" style={{ width: '100%' }} variant="outlined">
                  <InputLabel id="device-select">{intl('INPUT.LABEL.SELECT_CAMERA')}</InputLabel>
                  <Select
                    label={intl('INPUT.LABEL.SELECT_CAMERA')}
                    id="device-select"
                    value={device.deviceId}
                    onChange={(e) => {
                      const newDevice = devices.find((d) => d.deviceId === e.target.value);
                      if (!newDevice) return;
                      setDevice(newDevice);
                    }}>
                    {devices.map((device, i) => (
                      <MenuItem key={device.deviceId} value={device.deviceId}>
                        {device.label || `Camera ${i}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!disableScan && (
                  <BarcodeScannerComponent
                    delay={300}
                    videoConstraints={{ facingMode: 'environment', deviceId: device.deviceId }}
                    onUpdate={(err, result: any) => {
                      if (disableScan) return;
                      if (result) {
                        onScanSuccess(result.text);
                        setDisableScan(true);
                      }
                    }}
                  />
                )}
              </>
            )}
          </>
        )}
        <TextField
          className="scan-text"
          value={scanValue}
          placeholder="FOCUS HERE AND SCAN"
          onChange={onChangeScanText}
          disabled={mobileMode}
          fullWidth
        />
      </FormControlWrapper>
    </>
  );
};

export default Scanner;
